import React, { useState, useEffect } from "react";
import "./App.css";
import uploadIllustration from "./assets/image of first page.png";
import TopBarBeforeUpload from "./components/TopBarBeforeUpload";
import UploadBox from "./components/UploadBox";
import TopBarAfterUpload from "./components/TopBarAfterUpload";
import PhotoGrid from "./components/PhotoGrid";
import "@fortawesome/fontawesome-free/css/all.min.css";
import SelectionBar from "./components/SelectionBar";
import PrintOrderScreen from "./components/PrintOrderScreen";
import QuickOrderScreen from "./components/QuickOrderScreen";
import ReviewOrderScreen from "./components/ReviewOrderScreen";
import { sizes } from "./components/sizes";
import OrderModePopup from "./components/OrderModePopup";
import ShopSelectPopup from "./components/ShopSelectPopup";
import localforage from "localforage";


function App() {
  const [photos, setPhotos] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [sortOrder, setSortOrder] = useState("UPLOAD");
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [printOrderPhotos, setPrintOrderPhotos] = useState(null);
  const [printOrderIndex, setPrintOrderIndex] = useState(0);
  const [quickOrder, setQuickOrder] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [photoOverlays, setPhotoOverlays] = useState({});
  const [croppedImages, setCroppedImages] = useState({});
  const [orderType, setOrderType] = useState(null);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showPopup, setShowPopup] = useState(true);
  const [orderMode, setOrderMode] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const savedMode = params.get("orderMode");
    const savedShop = params.get("shop");

    // Only load saved mode/shop if they exist
    if (savedMode) setOrderMode(savedMode);
    if (savedMode === "shop" && savedShop) {
      setSelectedShop(savedShop);
    }
  }, []);

  useEffect(() => {
    const saveOrderData = async () => {
      const orderData = {
        photos,
        quantities,
        photoOverlays,
        orderMode,
        selectedShop,
        quickOrder,
        reviewMode,
      };
      await localforage.setItem("photoPrintOrderData", orderData);
    };

    // Save only when data changes
    if (photos.length > 0 || Object.keys(quantities).length > 0) {
      saveOrderData();
    }
  }, [photos, quantities, photoOverlays, orderMode, selectedShop, quickOrder, reviewMode]);

  useEffect(() => {
    const restoreData = async () => {
      const data = await localforage.getItem("photoPrintOrderData");
      if (data) {
        setPhotos(data.photos || []);
        setQuantities(data.quantities || {});
        setPhotoOverlays(data.photoOverlays || {});
        setOrderMode(data.orderMode || null);
        setSelectedShop(data.selectedShop || null);
        setQuickOrder(data.quickOrder || false);
        setReviewMode(data.reviewMode || false);
      }
    };

    // Restore only if navigation type is "back/forward"
    if (performance.getEntriesByType("navigation")[0]?.type === "back_forward") {
      restoreData();
    }
  }, []);

  useEffect(() => {
    const navEntry = performance.getEntriesByType("navigation")[0];

    if (navEntry && navEntry.type === "reload") {
      localforage.removeItem("photoPrintOrderData");
    }
  }, []);

  const handleAddPhotos = (e) => {
    const files = Array.from(e.target.files);

    const readAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result); // returns base64 string
        reader.onerror = reject;
        reader.readAsDataURL(file); // convert to base64
      });
    };

    Promise.all(files.map((file) => readAsBase64(file)))
      .then((base64Images) => {
        const previews = files.map((file, i) => ({
          file,
          url: base64Images[i], // ✅ Base64 instead of blob URL
          name: file.name,
        }));

        // initialize per-photo quantities
        const newQuantities = { ...quantities };
        previews.forEach((p) => {
          if (!newQuantities[p.name]) {
            const allSizes = sizes.flatMap(cat => cat.items || []);
            newQuantities[p.name] = Object.fromEntries(allSizes.map(s => [s.label, 0]));
          }

        });

        setPhotos((prev) => [...prev, ...previews]);
        setQuantities(newQuantities);
      })
      .catch((err) => {
        console.error("Error reading file as base64:", err);
        alert("Error uploading image. Please try again.");
      });
  };

  const openFileDialog = () => {
    document.getElementById("photoInput").click();
  };

  const handleSortChange = (order) => {
    setSortOrder(order);
  };

  const sortedPhotos = [...photos].sort((a, b) => {
    if (sortOrder === "AZ") return a.name.localeCompare(b.name);
    if (sortOrder === "ZA") return b.name.localeCompare(a.name);
    return 0; // ✅ "UPLOAD" keeps original order
  });

  const togglePhotoSelect = (name) => {
    setSelectedPhotos((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPhotos(sortedPhotos.map((p) => p.name));
    } else {
      setSelectedPhotos([]);
    }
  };

  const handleApplyFilter = (filter) => {
    setPhotos((prev) =>
      prev.map((photo) =>
        selectedPhotos.includes(photo.name)
          ? { ...photo, filterApplied: filter }
          : photo
      )
    );
  };

  const handlePhotoClick = (photo) => {
    const index = photos.findIndex((p) => p.name === photo.name);
    setPrintOrderPhotos(photos);
    setPrintOrderIndex(index);
  };

  if (!orderMode) {
    return (
      <>
        {/* Global Close Button */}
        <button
          className="global-close-btn"
          onClick={() => {
            window.location.href = "https://opticonframing.com.au/order-prints/";
          }}
        >
          ✕
        </button>

        <OrderModePopup
          onSelect={(mode) => {
            setOrderMode(mode);
            const url = new URL(window.location.href);
            url.searchParams.set("orderMode", mode);
            window.history.replaceState({}, "", url.toString());
          }}
        />
      </>
    );
  }

  if (orderMode === "shop" && !selectedShop) {
    return (
      <>
        {/* Global Close Button */}
        <button
          className="global-close-btn"
          onClick={() => window.history.back()}
        >
          ✕
        </button>

        <ShopSelectPopup
          onSelectShop={(shop) => {
            setSelectedShop(shop);
            const url = new URL(window.location.href);
            url.searchParams.set("shop", shop);
            window.history.replaceState({}, "", url.toString());
          }}
        />
      </>
    );
  }

  return (
    <div className="app-container">
      <input
        type="file"
        id="photoInput"
        accept="image/*"
        multiple
        onChange={handleAddPhotos}
        className="hidden-input"
      />

      {reviewMode ? (
        <ReviewOrderScreen
          photos={photos}
          quantities={quantities}
          setPhotos={setPhotos}
          setQuantities={setQuantities}
          setPhotoOverlays={setPhotoOverlays}
          selectedShop={selectedShop}   // ✅ new prop
          orderMode={orderMode}         // ✅ new prop
          onBack={() => {
            setReviewMode(false);
            setPrintOrderPhotos(null); // ensure PrintOrderScreen not open
            setQuickOrder(false);      // ensure QuickOrder not open
          }}
          onEditPhoto={(photo) => {          // ✅ pass edit handler
            const index = photos.findIndex((p) => p.name === photo.name);
            setPrintOrderPhotos(photos);
            setPrintOrderIndex(index);
            setReviewMode(false);            // close review mode & open PrintOrderScreen
          }}
          photoOverlays={photoOverlays}
          croppedImages={croppedImages}
          setCroppedImages={setCroppedImages}
        />
      ) : printOrderPhotos ? (
        <PrintOrderScreen
          selectedPhotos={printOrderPhotos}
          startIndex={printOrderIndex}
          onBack={() => setPrintOrderPhotos(null)}
          quantities={quantities}
          setQuantities={setQuantities}
          photoOverlays={photoOverlays}
          setPhotoOverlays={setPhotoOverlays}
          croppedImages={croppedImages}
          setCroppedImages={setCroppedImages}
        />
      ) : quickOrder ? (
        <QuickOrderScreen
          photos={photos}
          quantities={quantities}
          setQuantities={setQuantities}
          onReviewOrder={() => setReviewMode(true)}
        />
      ) : photos.length === 0 ? (
        <>
          <TopBarBeforeUpload openFileDialog={openFileDialog} />
          <main className="main-content">
            <UploadBox
              openFileDialog={openFileDialog}
              illustration={uploadIllustration}
            />
          </main>
        </>
      ) : (
        <>
          {selectedPhotos.length > 0 ? (
            <SelectionBar
              selectedCount={selectedPhotos.length}
              totalCount={sortedPhotos.length}
              onClear={() => setSelectedPhotos([])}
              onDelete={() =>
                setPhotos((prev) =>
                  prev.filter((p) => !selectedPhotos.includes(p.name))
                )
              }
              onSelectAll={handleSelectAll}
              allSelected={selectedPhotos.length === sortedPhotos.length}
              onApplyFilter={handleApplyFilter}
              photos={sortedPhotos}
              selectedPhotos={selectedPhotos}
              onQuickOrder={() => setQuickOrder(true)}

              // clear edits → reset overlays/filters
              onClearEdits={() => {
                setPhotoOverlays((prev) => {
                  const copy = { ...prev };
                  selectedPhotos.forEach((name) => {
                    delete copy[name]; // remove overlay
                  });
                  return copy;
                });
                setPhotos((prev) =>
                  prev.map((photo) =>
                    selectedPhotos.includes(photo.name)
                      ? { ...photo, filterApplied: null }
                      : photo
                  )
                );
                setSelectedPhotos([]);
              }}

              // ✅ clear sizes → reset quantities + clear cropped images + clear overlays
              onClearSizes={() => {
                setQuantities((prev) => {
                  const copy = { ...prev };
                  selectedPhotos.forEach((name) => {
                    if (copy[name]) {
                      Object.keys(copy[name]).forEach((size) => {
                        copy[name][size] = 0;
                      });
                    }
                  });
                  return copy;
                });

                // remove cropped images for the cleared photos
                setCroppedImages((prev) => {
                  const copy = { ...prev };
                  selectedPhotos.forEach((name) => {
                    if (copy[name]) delete copy[name];
                  });
                  return copy;
                });

                // remove overlays for cleared photos
                setPhotoOverlays((prev) => {
                  const copy = { ...prev };
                  selectedPhotos.forEach((name) => {
                    if (copy[name]) delete copy[name];
                  });
                  return copy;
                });

                setSelectedPhotos([]);
              }}

            />

          ) : (
            <TopBarAfterUpload
              openFileDialog={openFileDialog}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              onSelectAll={handleSelectAll}
              allSelected={selectedPhotos.length === sortedPhotos.length}
              quantities={quantities}
              setQuantities={setQuantities}
              onQuickOrder={() => setQuickOrder(true)}
              photos={sortedPhotos}
              onReviewOrder={() => setReviewMode(true)}
            />
          )}

          <main className="main-content">
            <PhotoGrid
              photos={sortedPhotos}
              selectedPhotos={selectedPhotos}
              onToggleSelect={togglePhotoSelect}
              onPhotoClick={handlePhotoClick}
              quantities={quantities}
              croppedImages={croppedImages}
            />
          </main>
        </>
      )}
    </div>
  );
}

export default App;
